import React, { useState, Component } from 'react';
import { BarChart, Bar, LineChart, Line, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell, ZAxis } from 'recharts';
import { ChevronRight, AlertCircle } from 'lucide-react';
// --- Mock Data ---
const lossRatioByYear = [{
  year: '2019',
  ratio: 58,
  status: 'good'
}, {
  year: '2020',
  ratio: 62,
  status: 'good'
}, {
  year: '2021',
  ratio: 68,
  status: 'acceptable'
}, {
  year: '2022',
  ratio: 72,
  status: 'poor'
}, {
  year: '2023',
  ratio: 65,
  status: 'acceptable'
}, {
  year: '2024',
  ratio: 61,
  status: 'good'
}];
const lossRatioTrend = [{
  month: 'Jan',
  glr: 62,
  elr: 58
}, {
  month: 'Feb',
  glr: 64,
  elr: 58
}, {
  month: 'Mar',
  glr: 66,
  elr: 59
}, {
  month: 'Apr',
  glr: 63,
  elr: 58
}, {
  month: 'May',
  glr: 67,
  elr: 60
}, {
  month: 'Jun',
  glr: 69,
  elr: 61
}, {
  month: 'Jul',
  glr: 65,
  elr: 60
}, {
  month: 'Aug',
  glr: 68,
  elr: 62
}, {
  month: 'Sep',
  glr: 64,
  elr: 61
}, {
  month: 'Oct',
  glr: 66,
  elr: 60
}, {
  month: 'Nov',
  glr: 63,
  elr: 59
}, {
  month: 'Dec',
  glr: 61,
  elr: 58
}];
const claimsData = [{
  name: 'Property 2024',
  frequency: 45,
  severity: 125,
  lossRatio: 61,
  exposure: 5000
}, {
  name: 'Casualty 2024',
  frequency: 52,
  severity: 145,
  lossRatio: 68,
  exposure: 4800
}, {
  name: 'Motor 2024',
  frequency: 78,
  severity: 85,
  lossRatio: 58,
  exposure: 6200
}, {
  name: 'Property 2023',
  frequency: 48,
  severity: 135,
  lossRatio: 65,
  exposure: 4500
}, {
  name: 'Casualty 2023',
  frequency: 55,
  severity: 155,
  lossRatio: 72,
  exposure: 4200
}, {
  name: 'Motor 2023',
  frequency: 82,
  severity: 92,
  lossRatio: 62,
  exposure: 5800
}, {
  name: 'Specialty 2024',
  frequency: 28,
  severity: 220,
  lossRatio: 74,
  exposure: 3200
}, {
  name: 'Specialty 2023',
  frequency: 32,
  severity: 245,
  lossRatio: 78,
  exposure: 2900
}];
const topLosses = [{
  rank: 1,
  claimRef: 'CLM-2024-892',
  date: '2024-11-15',
  insured: 'ABC Manufacturing Ltd',
  amount: 450000,
  status: 'Open'
}, {
  rank: 2,
  claimRef: 'CLM-2024-765',
  date: '2024-10-22',
  insured: 'XYZ Logistics plc',
  amount: 385000,
  status: 'Reserved'
}, {
  rank: 3,
  claimRef: 'CLM-2024-634',
  date: '2024-09-08',
  insured: 'Global Shipping Co',
  amount: 320000,
  status: 'Closed'
}, {
  rank: 4,
  claimRef: 'CLM-2024-521',
  date: '2024-08-14',
  insured: 'Tech Innovations Ltd',
  amount: 295000,
  status: 'Open'
}, {
  rank: 5,
  claimRef: 'CLM-2024-478',
  date: '2024-07-29',
  insured: 'Premier Construction',
  amount: 275000,
  status: 'Reserved'
}, {
  rank: 6,
  claimRef: 'CLM-2024-412',
  date: '2024-06-18',
  insured: 'Retail Group Holdings',
  amount: 245000,
  status: 'Closed'
}, {
  rank: 7,
  claimRef: 'CLM-2024-389',
  date: '2024-05-25',
  insured: 'Healthcare Services Inc',
  amount: 230000,
  status: 'Open'
}, {
  rank: 8,
  claimRef: 'CLM-2024-301',
  date: '2024-04-12',
  insured: 'Energy Solutions Ltd',
  amount: 215000,
  status: 'Closed'
}, {
  rank: 9,
  claimRef: 'CLM-2024-267',
  date: '2024-03-07',
  insured: 'Financial Services Corp',
  amount: 198000,
  status: 'Reserved'
}, {
  rank: 10,
  claimRef: 'CLM-2024-198',
  date: '2024-02-14',
  insured: 'Transport & Logistics',
  amount: 185000,
  status: 'Closed'
}];
// --- Colors ---
const COLORS = {
  good: '#3B82F6',
  acceptable: '#F59E0B',
  poor: '#DC2626',
  glr: '#DC2626',
  elr: '#3B82F6',
  neutral: '#E5E7EB' // Gray
};
// --- Helper Functions ---
const getBarColor = (status: string) => {
  switch (status) {
    case 'good':
      return COLORS.good;
    case 'acceptable':
      return COLORS.acceptable;
    case 'poor':
      return COLORS.poor;
    default:
      return COLORS.neutral;
  }
};
const getScatterColor = (lossRatio: number) => {
  if (lossRatio < 60) return COLORS.good;
  if (lossRatio <= 70) return COLORS.acceptable;
  return COLORS.poor;
};
const getStatusBadge = (status: string) => {
  const styles = {
    Open: 'bg-amber-100 text-amber-700',
    Reserved: 'bg-blue-100 text-blue-700',
    Closed: 'bg-green-100 text-green-700'
  };
  return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-700';
};
// --- Custom Tooltips ---
const CustomBarTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1759">
        <p className="font-semibold text-gray-900 mb-1" data-id="element-1760">
          {payload[0].payload.year}
        </p>
        <p className="text-gray-600" data-id="element-1761">
          Loss Ratio:{' '}
          <span className="font-medium text-gray-900" data-id="element-1762">{payload[0].value}%</span>
        </p>
        <p className="text-xs text-gray-400 mt-1" data-id="element-1763">Click to drill down</p>
      </div>;
  }
  return null;
};
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md text-sm" data-id="element-1764">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-1765">{data.name}</p>
        <div className="space-y-1" data-id="element-1766">
          <p className="text-gray-600" data-id="element-1767">
            Frequency: <span className="font-medium" data-id="element-1768">{data.frequency}</span>
          </p>
          <p className="text-gray-600" data-id="element-1769">
            Avg Severity: <span className="font-medium" data-id="element-1770">£{data.severity}K</span>
          </p>
          <p className="text-gray-600" data-id="element-1771">
            Loss Ratio: <span className="font-medium" data-id="element-1772">{data.lossRatio}%</span>
          </p>
          <p className="text-gray-600" data-id="element-1773">
            Exposure:{' '}
            <span className="font-medium" data-id="element-1774">
              £{(data.exposure / 1000).toFixed(1)}M
            </span>
          </p>
        </div>
        <p className="text-xs text-gray-400 mt-2" data-id="element-1775">Click to view details</p>
      </div>;
  }
  return null;
};
// --- Main Component ---
export function LossPerformanceRatios() {
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-1776">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1777">
        <div data-id="element-1778">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1779">
            Loss Performance & Ratios
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-1780">
            Underwriting discipline and claims behavior analysis • FY 2024
          </p>
        </div>
        <div className="flex gap-2" data-id="element-1781">
          <button className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1782">
            By Year
          </button>
          <button className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1783">
            By Class
          </button>
          <button className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1784">
            By Region
          </button>
        </div>
      </div>

      {/* Top Row - Two Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1785">
        {/* Loss Ratio by Year */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-96" data-id="element-1786">
          <div className="flex justify-between items-center mb-6" data-id="element-1787">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1788">
              Loss Ratio by Underwriting Year
            </h2>
            <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1789">
              <div className="flex items-center gap-1" data-id="element-1790">
                <div className="w-3 h-3 rounded bg-blue-600" data-id="element-1791"></div>
                <span data-id="element-1792">Good (&lt;60%)</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1793">
                <div className="w-3 h-3 rounded bg-amber-500" data-id="element-1794"></div>
                <span data-id="element-1795">Acceptable</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-1796">
                <div className="w-3 h-3 rounded bg-red-600" data-id="element-1797"></div>
                <span data-id="element-1798">Poor (&gt;70%)</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1799">
            <BarChart data={lossRatioByYear} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1800">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1801" />
              <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1802" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `${value}%`} domain={[0, 100]} data-id="element-1803" />
              <Tooltip content={<CustomBarTooltip data-id="element-1805" />} data-id="element-1804" />
              <ReferenceLine y={65} stroke="#9CA3AF" strokeDasharray="3 3" label={{
              value: 'Target 65%',
              position: 'right',
              fill: '#6B7280',
              fontSize: 12
            }} data-id="element-1806" />
              <Bar dataKey="ratio" radius={[4, 4, 0, 0]} cursor="pointer" onMouseEnter={(_, index) => setHoveredBar(index)} onMouseLeave={() => setHoveredBar(null)} data-id="element-1807">
                {lossRatioByYear.map((entry, index) => <Cell key={`cell-${index}`} fill={getBarColor(entry.status)} opacity={hoveredBar === null || hoveredBar === index ? 1 : 0.6} data-id="element-1808" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* GLR vs ELR Trend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-96" data-id="element-1809">
          <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-1810">
            GLR vs ELR Trend
          </h2>
          <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1811">
            <LineChart data={lossRatioTrend} margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-1812">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-1813" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} dy={10} data-id="element-1814" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#6B7280',
              fontSize: 12
            }} tickFormatter={value => `${value}%`} domain={[50, 75]} data-id="element-1815" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px'
            }} formatter={(value: any) => `${value}%`} data-id="element-1816" />
              <Legend verticalAlign="top" align="right" wrapperStyle={{
              paddingBottom: '20px'
            }} iconType="line" data-id="element-1817" />
              <ReferenceLine y={65} stroke="#9CA3AF" strokeDasharray="3 3" label={{
              value: 'Target',
              position: 'right',
              fill: '#6B7280',
              fontSize: 12
            }} data-id="element-1818" />
              <Line type="monotone" dataKey="glr" name="Gross Loss Ratio" stroke={COLORS.glr} strokeWidth={2} dot={{
              r: 3
            }} activeDot={{
              r: 6
            }} data-id="element-1819" />
              <Line type="monotone" dataKey="elr" name="Expected Loss Ratio" stroke={COLORS.elr} strokeWidth={2} dot={{
              r: 3
            }} activeDot={{
              r: 6
            }} data-id="element-1820" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Middle Row - Scatter Plot */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-96" data-id="element-1821">
        <div className="flex justify-between items-center mb-6" data-id="element-1822">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1823">
            Claims Frequency vs Severity Analysis
          </h2>
          <p className="text-xs text-gray-500" data-id="element-1824">
            Bubble size represents exposure
          </p>
        </div>
        <ResponsiveContainer width="100%" height="100%" minHeight={280} data-id="element-1825">
          <ScatterChart margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5
        }} data-id="element-1826">
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-1827" />
            <XAxis type="number" dataKey="frequency" name="Frequency" axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 12
          }} label={{
            value: 'Claims Frequency',
            position: 'insideBottom',
            offset: -5,
            fill: '#6B7280'
          }} data-id="element-1828" />
            <YAxis type="number" dataKey="severity" name="Severity" axisLine={false} tickLine={false} tick={{
            fill: '#6B7280',
            fontSize: 12
          }} tickFormatter={value => `£${value}K`} label={{
            value: 'Average Severity',
            angle: -90,
            position: 'insideLeft',
            fill: '#6B7280'
          }} data-id="element-1829" />
            <ZAxis type="number" dataKey="exposure" range={[100, 1000]} data-id="element-1830" />
            <Tooltip content={<CustomScatterTooltip data-id="element-1832" />} data-id="element-1831" />
            <ReferenceLine x={60} stroke="#E5E7EB" strokeDasharray="3 3" data-id="element-1833" />
            <ReferenceLine y={150} stroke="#E5E7EB" strokeDasharray="3 3" data-id="element-1834" />
            <Scatter name="Business Lines" data={claimsData} cursor="pointer" data-id="element-1835">
              {claimsData.map((entry, index) => <Cell key={`cell-${index}`} fill={getScatterColor(entry.lossRatio)} data-id="element-1836" />)}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom Row - Top 10 Losses Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1837">
        <div className="flex justify-between items-center mb-6" data-id="element-1838">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-1839">
            Top 10 Largest Losses
          </h2>
          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center" data-id="element-1840">
            View All Claims
            <ChevronRight size={16} className="ml-1" data-id="element-1841" />
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-1842">
          <table className="w-full" data-id="element-1843">
            <thead data-id="element-1844">
              <tr className="border-b border-gray-200" data-id="element-1845">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1846">
                  Rank
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1847">
                  Claim Ref
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1848">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1849">
                  Insured
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1850">
                  Loss Amount
                </th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1851">
                  Status
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1852">
              {topLosses.map(loss => <tr key={loss.rank} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors" onMouseEnter={() => setHoveredRow(loss.rank)} onMouseLeave={() => setHoveredRow(null)} data-id="element-1853">
                  <td className="py-3 px-4 text-sm text-gray-900 font-medium" data-id="element-1854">
                    {loss.rank}
                  </td>
                  <td className="py-3 px-4 text-sm text-blue-600 font-medium" data-id="element-1855">
                    {loss.claimRef}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600" data-id="element-1856">
                    {loss.date}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-900" data-id="element-1857">
                    {loss.insured}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-red-600" data-id="element-1858">
                    £{loss.amount.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1859">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(loss.status)}`} data-id="element-1860">
                      {loss.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center justify-between text-xs text-gray-500" data-id="element-1861">
          <p data-id="element-1862">Showing 10 of 247 total claims</p>
          <p className="flex items-center" data-id="element-1863">
            <AlertCircle size={14} className="mr-1" data-id="element-1864" />
            Click any row to view claim details
          </p>
        </div>
      </div>
    </div>;
}